import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { projectIdParamsSchema } from '../schemas';
import { fullMergedTagSchema } from '@shared/interfaces/IMergedTag';
import { baseFloorSchema } from '@shared/interfaces/IBaseFloor';

// ============================================================
// Response Schemas (inline — service-local types can't be imported from shared)
// ============================================================

const professionWorkerDataWithIdSchema = z.object({
	profession: z.object({ _id: z.string() }),
	workers: z.array(fullMergedTagSchema),
	floors: z.array(z.object({ floor: baseFloorSchema, workersCount: z.number() })),
});

const floorWorkerDataWithIdSchema = z.object({
	floor: baseFloorSchema,
	workers: z.array(fullMergedTagSchema),
	professions: z.array(
		z.object({
			profession: z.object({ _id: z.string() }),
			workersCount: z.number(),
		})
	),
});

// ============================================================
// Query Schemas
// ============================================================

const workersByProfessionsQuerySchema = z.object({
	professionsIds: z.array(z.string()).optional(),
	floorId: z.string().optional(),
	areaId: z.string().optional(),
});

const workersByFloorsQuerySchema = z.object({
	floorsIds: z.array(z.string()).optional(),
});

// ============================================================
// Contract
// ============================================================

/**
 * `{ root: true }`, so each path is spelled in full rather than inheriting the
 * `mobile/workers` prefix. Both routes now sit alongside
 * `/projects/:projectId/workers` from `mergedData.contract.ts` — same URL
 * space, two different route files, which is why the tests assert absolute
 * URLs rather than trusting a prefix.
 */
export const mobileWorkersContract = defineContract(
	'mobile/workers',
	{
		getWorkersByProfessions: route({
			method: 'GET',
			path: '/projects/:projectId/workers/byProfessions',
			params: projectIdParamsSchema,
			query: workersByProfessionsQuerySchema,
			response: z.array(professionWorkerDataWithIdSchema),
			operationId: 'getWorkersByProfessions',
			tags: ['Mobile Workers'],
			summary: 'Get workers data grouped by professions',
		}),
		getWorkersByFloors: route({
			method: 'GET',
			path: '/projects/:projectId/workers/byFloors',
			params: projectIdParamsSchema,
			query: workersByFloorsQuerySchema,
			response: z.array(floorWorkerDataWithIdSchema),
			operationId: 'getWorkersByFloors',
			tags: ['Mobile Workers'],
			summary: 'Get workers data grouped by floors',
		}),
	},
	{ root: true }
);
